import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { ProdutoContrato } from "../../../../types/contratos";
import { SaldoItemContrato } from "../../../../types/empenhos";
import { apiService } from "../../../../services/api";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ProtectedValue } from "../../../../components/ProtectedValue";

interface ItensOportunidadeProps {
    produtos: ProdutoContrato[];
    formatCurrency: (value: number | string) => string;
    contratoId?: number;
}

const ItensOportunidade: React.FC<ItensOportunidadeProps> = ({
    produtos,
    formatCurrency,
    contratoId
}) => {
    const { hasPermission } = usePermissions();
    const podeVerValores = hasPermission('AUTORIZACOES', 'VER_VALORES');

    const [produtosOrdenados, setProdutosOrdenados] = useState<ProdutoContrato[]>([]);
    const [totais, setTotais] = useState<{
        total_referencia: number;
        total_orcado: number;
        total_proposta: number;
        total_geral: number;
    }>({
        total_referencia: 0,
        total_orcado: 0,
        total_proposta: 0,
        total_geral: 0
    });

    // Estados para o card de informações
    const [produtoInfoVisivel, setProdutoInfoVisivel] = useState<number | null>(null);
    const infoCardRef = useRef<HTMLDivElement>(null);

    const [saldosPorProduto, setSaldosPorProduto] = useState<Record<number, SaldoItemContrato>>({});

    useEffect(() => {
        if (!contratoId) return;
        apiService.getSaldoMestreContrato(contratoId)
            .then(res => {
                if (res.success && res.data) {
                    const map: Record<number, SaldoItemContrato> = {};
                    res.data.forEach((s: SaldoItemContrato) => {
                        map[s.id_produto_oportunidade] = s;
                    });
                    setSaldosPorProduto(map);
                }
            })
            .catch(err => console.error('Erro ao carregar saldo de itens:', err));
    }, [contratoId]);

    // Função para ordenar produtos por número de edital ou id
    const ordenarProdutos = (produtosParaOrdenar: ProdutoContrato[]) => {
        const getNumeroEditalFromString = (edital: string | null | undefined): number => {
            if (!edital || typeof edital !== 'string' || edital.trim() === '') {
                return 0;
            }
            const match = edital.match(/\d+/);
            if (!match || !match[0]) {
                return 0;
            }
            const parsedNum = parseInt(match[0], 10);
            return isNaN(parsedNum) ? 0 : parsedNum;
        };

        return [...produtosParaOrdenar].sort((a, b) => {
            const numeroEditalA = getNumeroEditalFromString(a.numero_edital);
            const numeroEditalB = getNumeroEditalFromString(b.numero_edital);

            const aTemEditalValido = numeroEditalA !== 0;
            const bTemEditalValido = numeroEditalB !== 0;

            if (aTemEditalValido && !bTemEditalValido) {
                return -1;
            }
            if (!aTemEditalValido && bTemEditalValido) {
                return 1;
            }

            if (aTemEditalValido && bTemEditalValido) {
                if (numeroEditalA !== numeroEditalB) {
                    return numeroEditalA - numeroEditalB;
                }
                // Usar id ou id_produto_oportunidade como desempate
                const idA = a.id ?? a.id_produto_oportunidade;
                const idB = b.id ?? b.id_produto_oportunidade;
                return idA - idB;
            }

            // Usar id ou id_produto_oportunidade como ordenação final
            const idA = a.id ?? a.id_produto_oportunidade;
            const idB = b.id ?? b.id_produto_oportunidade;
            return idA - idB;
        });
    };

    // Atualizar produtos ordenados sempre que a lista de produtos mudar
    useEffect(() => {
        setProdutosOrdenados(ordenarProdutos(produtos));
    }, [produtos]);

    // Fechar o card ao clicar fora dele
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (infoCardRef.current &&
                !infoCardRef.current.contains(event.target as Node) &&
                !(event.target as Element).classList.contains('info-button')) {
                setProdutoInfoVisivel(null);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Função para mostrar/esconder o card de informações
    const toggleInfoCard = (e: React.MouseEvent, produtoId: number) => {
        e.stopPropagation();
        e.preventDefault();
        setProdutoInfoVisivel(produtoInfoVisivel === produtoId ? null : produtoId);
    };

    // Função para formatar números (quantidades) que vêm como string do banco
    const formatQuantity = (value: string | number) => {
        // Converter para número caso seja string
        const numValue = typeof value === 'string' ? parseFloat(value) : value;

        // Verifica se é um número válido
        if (isNaN(numValue)) return '0';

        // Verifica se o valor é um número inteiro
        if (Number.isInteger(numValue)) {
            // Formata sem casas decimais
            return numValue.toString();
        } else {
            // Formata com casas decimais (usando vírgula)
            return numValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2
            });
        }
    };

    // Função para obter a cor da margem com base no valor
    const getMargemColor = (margem: string | number | null | undefined): string => {
        if (!margem) return '';

        // Converter para número se for string
        const numValue = typeof margem === 'string' ? parseFloat(margem) : margem;

        if (isNaN(numValue)) return '';
        if (numValue <= 8) return 'bg-danger';
        if (numValue < 15) return 'bg-warning';
        return 'bg-success';
    };

    const formatarPorcentagem = (valor: string | number | null | undefined): string => {
        if (valor === null || valor === undefined || valor === '') return '-';

        // Garantir que valor seja um número
        const numValue = typeof valor === 'string' ? parseFloat(valor) : valor;

        // Verificar se é um número válido
        if (isNaN(numValue)) return '-';

        return numValue.toFixed(2) + '%';
    };

    // Calcular totais quando os produtos mudarem
    useEffect(() => {
        if (produtos && produtos.length > 0) {
            // Função auxiliar para converter string para número
            const toNumber = (value: string | number | null | undefined): number => {
                if (value === null || value === undefined) return 0;
                if (typeof value === 'number') return value;
                return parseFloat(value) || 0;
            };

            const totalRef = produtos.reduce((acc, p) => {
                const qtd = toNumber(p.quantidade);
                const valorRef = toNumber(p.valor_referencia);
                return acc + (qtd * valorRef);
            }, 0);

            const totalOrcado = produtos.reduce((acc, p) => {
                if (p.valor_orcado) {
                    const qtd = toNumber(p.quantidade);
                    const valorOrcado = toNumber(p.valor_orcado);
                    return acc + (qtd * valorOrcado);
                }
                return acc;
            }, 0);

            const totalProposta = produtos.reduce((acc, p) => {
                if (p.valor_proposta) {
                    const qtd = toNumber(p.quantidade);
                    const valorProposta = toNumber(p.valor_proposta);
                    return acc + (qtd * valorProposta);
                }
                return acc;
            }, 0);

            // Total geral seguindo a lógica da coluna individual
            const totalGeral = produtos.reduce((acc, p) => {
                const qtd = toNumber(p.quantidade);
                const valorProposta = toNumber(p.valor_proposta);
                const valorRef = toNumber(p.valor_referencia);
                const valorUsar = valorProposta > 0 ? valorProposta : valorRef;
                return acc + (qtd * valorUsar);
            }, 0);

            setTotais({
                total_referencia: totalRef,
                total_orcado: totalOrcado,
                total_proposta: totalProposta,
                total_geral: totalGeral
            });
        } else {
            setTotais({
                total_referencia: 0,
                total_orcado: 0,
                total_proposta: 0,
                total_geral: 0
            });
        }
    }, [produtos]);

    // Função para copiar descrição do produto
    const copyDescricao = (produto: ProdutoContrato) => {
        const textoParaCopiar = `${produto.produto_nome}\n\nDescrição: ${produto.descricao_especifica || produto.descricao || 'Sem descrição disponível'}`;
        navigator.clipboard.writeText(textoParaCopiar);
        toast.success("Descrição copiada para a área de transferência!");
    };

    return (
        <div className="card">
            <div className="card-header">
                <h4 className="fw-semibold">Itens do Contrato</h4>
            </div>
            <div className="card-body">
                {produtos.length === 0 ? (
                    <div className="alert alert-info">
                        Nenhum produto registrado para este contrato.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped custom-table mb-0">
                            <thead>
                                <tr>
                                    <th>N° Edital</th>
                                    <th>Produto</th>
                                    <th>Marca</th>
                                    <th>Qtd.</th>
                                    {contratoId && <th>Qtd. Emp.</th>}
                                    <th>Valor Ref.</th>
                                    <th>Valor Orçado</th>
                                    <th>Valor Proposta</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {produtosOrdenados.map((produto) => {
                                    // Função auxiliar para converter string para número
                                    const toNumber = (value: string | number | null | undefined): number => {
                                        if (!value) return 0;
                                        if (typeof value === 'number') return value;
                                        return parseFloat(value) || 0;
                                    };

                                    // Calcular o valor total com base no valor proposta ou referência
                                    const qtd = toNumber(produto.quantidade);
                                    const valorProposta = toNumber(produto.valor_proposta);
                                    const valorRef = toNumber(produto.valor_referencia);
                                    const valorUsar = valorProposta > 0 ? valorProposta : valorRef;
                                    const total = qtd * valorUsar;

                                    return (
                                        <tr key={produto.id || produto.id_produto_oportunidade}>
                                            {/* Coluna para Número do Edital */}
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '80px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={produto.numero_edital || ''}
                                                >
                                                    {produto.numero_edital || <span className="text-muted">-</span>}
                                                </div>
                                            </td>
                                            <td style={{ position: 'relative', overflow: 'visible' }}>
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        style={{
                                                            maxWidth: '140px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                        title={produto.produto_nome}
                                                    >
                                                        {produto.produto_nome}
                                                    </div>
                                                    <button
                                                        className="ms-2 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center info-button"
                                                        onClick={(e) => toggleInfoCard(e, produto.id || produto.id_produto_oportunidade)}
                                                        style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            border: 'none',
                                                            cursor: 'pointer'
                                                        }}
                                                        title="Ver informações detalhadas"
                                                    >
                                                        i
                                                    </button>

                                                    {produtoInfoVisivel === (produto.id || produto.id_produto_oportunidade) && (
                                                        <div
                                                            ref={infoCardRef}
                                                            className="position-absolute start-100 top-0 ms-2 bg-white rounded shadow-lg border border-gray-200"
                                                            style={{
                                                                zIndex: 9999,
                                                                width: '280px',
                                                                maxWidth: '90vw'
                                                            }}
                                                        >
                                                            <div className="p-3">
                                                                <h6
                                                                    className="fw-bold text-primary mb-2"
                                                                    style={{
                                                                        maxWidth: '100%',
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                    title={produto.produto_nome}
                                                                >
                                                                    {produto.produto_nome}
                                                                </h6>
                                                                <p className="small mb-3" style={{ color: '#555' }}>
                                                                    {produto.descricao_especifica || produto.descricao || 'Sem descrição disponível'}
                                                                </p>
                                                                {produto.marca && (
                                                                    <p className="small mb-2">
                                                                        <strong>Marca:</strong> {produto.marca}
                                                                    </p>
                                                                )}
                                                                {/* Indicador de origem do produto */}
                                                                {produto.origem && (
                                                                    <p className="small mb-2">
                                                                        <span
                                                                            className={`badge ${produto.origem === 'snapshot'
                                                                                    ? 'bg-warning'
                                                                                    : 'bg-info'
                                                                                }`}
                                                                            style={{ fontSize: '10px' }}
                                                                        >
                                                                            {produto.origem === 'snapshot'
                                                                                ? 'Valor Histórico'
                                                                                : 'Valor Atual'}
                                                                        </span>
                                                                    </p>
                                                                )}
                                                                <div className="d-flex justify-content-end mt-2">
                                                                    <button
                                                                        onClick={() => copyDescricao(produto)}
                                                                        className="btn btn-sm btn-outline-primary me-2"
                                                                    >
                                                                        <i className="ti ti-copy"></i> Copiar
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setProdutoInfoVisivel(null)}
                                                                        className="btn btn-sm btn-light"
                                                                    >
                                                                        <i className="ti ti-x"></i> Fechar
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        maxWidth: '120px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={produto.marca ?? 'Sem marca'}
                                                >
                                                    {produto.marca ? produto.marca : <span className="text-muted">-</span>}
                                                </div>
                                            </td>
                                            <td>{formatQuantity(produto.quantidade)}</td>
                                            {contratoId && (() => {
                                                const saldo = saldosPorProduto[produto.id_produto_oportunidade];
                                                if (!saldo) return <td className="text-muted">-</td>;
                                                const empenhada = Number(saldo.quantidade_empenhada);
                                                const contratada = Number(saldo.quantidade_contrato);
                                                const pct = contratada > 0 ? (empenhada / contratada) * 100 : 0;
                                                const badgeClass = pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success';
                                                return (
                                                    <td>
                                                        <span className={`badge ${badgeClass}`}>
                                                            {formatQuantity(empenhada)} / {formatQuantity(contratada)}
                                                        </span>
                                                    </td>
                                                );
                                            })()}
                                            <td><ProtectedValue module="AUTORIZACOES" value={formatCurrency(produto.valor_referencia)} /></td>
                                            <td>
                                                {produto.valor_orcado
                                                    ? formatCurrency(produto.valor_orcado)
                                                    : '-'}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {produto.valor_proposta
                                                        ? <ProtectedValue module="AUTORIZACOES" value={formatCurrency(produto.valor_proposta)} />
                                                        : '-'}

                                                    {/* Exibir o badge de margem */}
                                                    {produto.margem_lucro && produto.valor_proposta && (
                                                        <span className={`badge ${getMargemColor(produto.margem_lucro)} rounded-pill`}>
                                                            {formatarPorcentagem(produto.margem_lucro)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td><strong><ProtectedValue module="AUTORIZACOES" value={formatCurrency(total)} /></strong></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className="bg-light">
                                    <td colSpan={contratoId ? 8 : 7}><strong>TOTAIS</strong></td>
                                    <td><strong><ProtectedValue module="AUTORIZACOES" value={formatCurrency(totais.total_geral)} /></strong></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}

                {/* Legenda */}
                {produtos.length > 0 && (
                    <div className="mt-3 alert alert-info small">
                        <i className="ti ti-info-circle me-2"></i>
                        <strong>Visualização:</strong> Esta tabela mostra os itens do contrato com seus valores já considerando snapshot histórico (se aplicável).
                        <br />
                        <strong>Origem dos valores:</strong>
                        <span className="badge bg-warning ms-1">Valor Histórico</span> = Salvo no momento da renovação,
                        <span className="badge bg-info ms-1">Valor Atual</span> = Valor em tempo real.
                        <br />
                        <strong>Margem de lucro:</strong> <span className="badge bg-danger">≤ 8%</span>, <span className="badge bg-warning">8-15%</span>, <span className="badge bg-success">&gt; 15%</span>.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ItensOportunidade;